/**
 * Illustrative data for the homepage Operations preview (operational data
 * logging + maintenance logging). Not real customer data.
 *
 * Series are generated from a small deterministic PRNG rather than
 * Math.random() so the demo looks identical on every render and every visit —
 * a reshuffling chart on a marketing page reads as a glitch.
 */

/** Deterministic LCG — same seed always yields the same series. */
const seeded = (seed) => {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
};

/**
 * @param {number} seed
 * @param {number} base     starting value
 * @param {number} drift    total change across the window
 * @param {number} noise    peak-to-peak jitter
 * @param {number} decimals rounding, matching the column's `decimals` setting
 */
const series = (seed, base, drift, noise, decimals, n = 30) => {
  const rand = seeded(seed);
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const trend = base + (drift * i) / (n - 1);
    const wobble = (rand() - 0.5) * noise;
    // A gentle weekly cycle keeps the shape looking operational, not synthetic.
    const weekly = Math.sin((i / 7) * Math.PI * 2) * noise * 0.35;
    out.push(Number((trend + wobble + weekly).toFixed(decimals)));
  }
  return out;
};

const DAYS = 30;

/** Date keys for the last 30 days, so the demo never looks stale. */
export const DEMO_DATES = (() => {
  const out = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = DAYS - 1; i >= 0; i -= 1) {
    const d = new Date(today.getTime() - i * 86400000);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
})();

/**
 * Two user-defined logs, mirroring the real model: each column carries its own
 * label, free-text unit and decimal precision. `sparse` marks row indices left
 * blank, because real sheets have gaps and the table renders those as "—".
 */
export const DEMO_LOGS = [
  {
    id: "pad",
    name: "Daily Pad Operations",
    // The first two columns are preselected on the chart, so they are ordered
    // to share a magnitude — two flows. Pairing a tonnage with a flow rate left
    // one series flattened against the axis.
    columns: [
      { key: "flow", label: "PLS Flow", unit: "m³/h", decimals: 1, color: "#4caf50", values: series(23, 412, 46, 38, 1) },
      { key: "raff", label: "Raffinate Flow", unit: "m³/h", decimals: 1, color: "#B6B6DE", values: series(83, 447, 38, 31, 1) },
      { key: "stacked", label: "Ore Stacked", unit: "t", decimals: 0, color: "#9B9BD2", values: series(7, 8200, 1400, 900, 0) },
      { key: "grade", label: "PLS Grade", unit: "g/t", decimals: 2, color: "#ff9800", values: series(41, 0.94, -0.11, 0.09, 2) },
    ],
    sparse: { grade: [26, 27] },
  },
  {
    id: "plant",
    name: "Plant Performance",
    columns: [
      { key: "feed", label: "Plant Feed", unit: "m³/h", decimals: 1, color: "#B6B6DE", values: series(11, 388, 52, 30, 1) },
      { key: "barren", label: "Barren Return", unit: "m³/h", decimals: 1, color: "#26a69a", values: series(97, 371, 44, 27, 1) },
      { key: "rec", label: "Plant Recovery", unit: "%", decimals: 1, color: "#9167a3", values: series(29, 88.4, 2.6, 1.8, 1) },
      { key: "acid", label: "Acid Consumption", unit: "kg/t", decimals: 1, color: "#f44336", values: series(67, 6.8, -0.6, 0.7, 1) },
    ],
    sparse: { acid: [28] },
  },
];

/* ─── Maintenance ────────────────────────────────────────────────── */

/** Asset hierarchy: facility (0) > area (1) > device (2) > part (3). */
export const DEMO_ASSETS = [
  { id: "pad3", name: "Heap Leach Pad 3", type: "facility", depth: 0, status: "operational" },
  { id: "irr", name: "Irrigation Network", type: "area", depth: 1, status: "attention", parent: "pad3" },
  { id: "pump-a", name: "Booster Pump A", type: "device", depth: 2, status: "down", parent: "irr" },
  { id: "seal", name: "Impeller Seal Kit", type: "part", depth: 3, status: "operational", parent: "pump-a" },
  { id: "manifold", name: "Drip Line Manifold", type: "device", depth: 2, status: "attention", parent: "irr" },
  { id: "ponds", name: "Pond Farm", type: "area", depth: 1, status: "operational", parent: "pad3" },
  { id: "pls-pump", name: "PLS Transfer Pump", type: "device", depth: 2, status: "operational", parent: "ponds" },
  { id: "plant", name: "SX/EW Plant", type: "facility", depth: 0, status: "operational" },
  { id: "column", name: "Carbon Column #3", type: "device", depth: 2, status: "operational", parent: "plant" },
];

/** `days` = how long ago the entry was logged, so dates stay recent. */
export const DEMO_MAINT_LOGS = [
  {
    id: "l1", assetId: "pump-a", type: "problem", status: "open", days: 1,
    title: "Discharge pressure dropping under load",
    by: "T. Nakamura", downtime: 6.5, parts: null, cost: null,
  },
  {
    id: "l2", assetId: "manifold", type: "inspection", status: "in_progress", days: 3,
    title: "Emitter flow survey — rows 14 to 22",
    by: "L. Duarte", downtime: null, parts: null, cost: null,
  },
  {
    id: "l3", assetId: "seal", type: "replacement", status: "resolved", days: 6,
    title: "Replaced mechanical seal after weep detected",
    by: "T. Nakamura", downtime: 4, parts: "Seal kit SK-204", cost: 1840,
  },
  {
    id: "l4", assetId: "pls-pump", type: "maintenance", status: "resolved", days: 9,
    title: "1,000 h service — bearings greased, alignment checked",
    by: "Contractor — Andes Mech.", downtime: 2.5, parts: null, cost: 620,
  },
  {
    id: "l5", assetId: "column", type: "repair", status: "resolved", days: 12,
    title: "Reseated leaking flange on carbon transfer line",
    by: "M. Boateng", downtime: 1.5, parts: "2× gasket G-88", cost: 210,
  },
  {
    id: "l6", assetId: "irr", type: "note", status: "resolved", days: 15,
    title: "Seasonal irrigation set-points revised for winter",
    by: "S. Whitfield", downtime: null, parts: null, cost: null,
  },
  // Entries logged at facility and area level, so selecting a parent with
  // "include sub-assets" switched off still shows its own history.
  {
    id: "l7", assetId: "pad3", type: "inspection", status: "resolved", days: 18,
    title: "Quarterly pad liner and berm walkover",
    by: "L. Duarte", downtime: null, parts: null, cost: null,
  },
  {
    id: "l8", assetId: "ponds", type: "maintenance", status: "resolved", days: 22,
    title: "Freeboard survey and sediment clearance",
    by: "Contractor — Andes Mech.", downtime: null, parts: null, cost: 3400,
  },
];

export const ASSET_META = {
  facility: { icon: "🏭", label: "Facility" },
  area: { icon: "📍", label: "Area" },
  device: { icon: "⚙️", label: "Device / Machine" },
  part: { icon: "🔩", label: "Part" },
};

export const LOG_TYPE_META = {
  problem: { icon: "⚠️", label: "Problem" },
  repair: { icon: "🔧", label: "Repair" },
  replacement: { icon: "♻️", label: "Replacement" },
  maintenance: { icon: "🛠️", label: "Maintenance" },
  inspection: { icon: "🔍", label: "Inspection" },
  note: { icon: "📝", label: "Note" },
};

export const LOG_STATUS_LABEL = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
};

export const ASSET_STATUS_LABEL = {
  operational: "Operational",
  attention: "Needs Attention",
  down: "Down",
  retired: "Retired",
};
